// src/features/townBudget/model/BuildTownBudgetModal.jsx

/**
 * BuildTownBudgetModal.jsx
 *
 * “Build a Budget Workshop” modal for Glastonbury Budget app.
 *
 * Primary responsibilities:
 * 1) Resolve a single “anchor” fiscal start year that is present across ALL required Firestore docs.
 * 2) Load expenditures (Town, Debt Service & Transfers Out, Education) as “section” arrays.
 * 3) Load revenues (6 revenue docs) as one combined CommonTable data set (sectioned by doc).
 * 4) Seed a synthetic editable column `buildBudget` across ALL tables.
 * 5) Compute deltas:
 *    - Expenditure change: (buildBudget - baseline y0_adopted)
 *    - Revenue change:     (buildBudget - baseline y0_adopted)
 *    - Net levy delta:     (expenditure change) - (revenue change)
 * 6) Display implied changes in levy and mill rate at the top of the modal.
 *
 * IMPORTANT:
 * - This file is “UI orchestration”: it relies heavily on helpers in:
 *   - ./utils/buildBudgetWorkshopUtils.js
 *   - ../Town/utils/revenueTableUtils.js
 *   - ../Town/utils/fetchRevenueData.js
 *   - ../Town/utils/fetchExpenditureSections.js
 * - Many pieces are memoized because tables can be large and we want stable renders.
 *
 * Fiscal year convention:
 * - The app uses START YEAR keys.
 * - Example: startYear=2025 => FY 2025–2026
 */

import React, { useEffect, useMemo, useState, useCallback } from "react";
import { getDoc, doc } from "firebase/firestore";
import { db } from "../../../firebase.js";
import { XMarkIcon } from "@heroicons/react/24/solid";

import { CommonTable } from "../../../shared/ui/table/CommonTable.jsx";
import { buildBuildBudgetColumns } from "../../../budget/model/columns/columnBuilders.js";

import {
  applySectionRowOrder,
  buildIntergovDisplayData,
  createSectionChangeHandler,
} from "../utils/revenueTableUtils.js";

import { fetchExpenditureSections } from "../data/fetchExpenditureSections.js";
import { fetchRevenueData } from "../data/fetchRevenueData.js";

import { TOWN_SECTION_ORDER } from "../../../shared/constants/tableOrder.js";
import { useMillRate } from "../../../budget/data/millRate/millRate.js";
import { useGrandListHistory } from "../../../budget/data/grandList/grandList.js";
import {
  fmtMillRate,
  fmtDollars0,
  pickLatestCurrentLevy,
  seedBuildBudget,
  populateBuildBudgetTotals,
  applyBuildBudgetDelta,
  computeWorkshopTotals,
  computeUpdatedLevy,
  pickBaselineMillRate,
  parseCurrencyLike,
  injectRevenueSectionTotals,
  buildRevenueRowsFromDoc,
} from "../../../budget/model/workshop/buildBudgetWorkshopUtils.js";

/**
 * Build a Budget Workshop modal.
 *
 * Data model conventions used by CommonTable rows:
 * - `label` (string) row label
 * - `section` (string|truthy) when the row is a *section header* row (non-numeric)
 * - year columns like `y2_actual`, `y1_adopted`, `y0_adopted` are numeric
 * - `buildBudget` is the editable numeric column used for “what-if”
 *
 * IMPORTANT:
 * - We keep editing constrained to buildBudget column only.
 * - We intentionally avoid editing Education here (it is sourced and treated as canonical).
 */
export function BuildBudgetModal({
  open,
  onClose,
  title = "Build a Budget Workshop",
  initialYear = null,
  town = "Glastonbury",
  millRateDocPath = { collection: "common", id: "millRate" },
  taxRevenuesDocPath = {
    collection: "townGovernment",
    id: "revenues_tax_revenues",
  },
}) {
  /**
   * UI state
   */
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  /**
   * Mill rate baseline (from Firestore, and possibly overridden by CT lookup hook)
   */
  const [baseMillRate, setBaseMillRate] = useState(null);

  /**
   * Levy (derived from Tax Revenues doc – used as baseline levy for what-if)
   */
  const [storedCurrentLevy, setStoredCurrentLevy] = useState(null);
  const [levyLoading, setLevyLoading] = useState(false);

  /**
   * Diagnostics-only: max year present in Tax Revenues doc (not used as anchor)
   */
  const [dataMaxYear, setDataMaxYear] = useState(null);

  /**
   * Expenditure sections.
   * Each is stored as an array of “rows” / “section objects” compatible with revenueTableUtils helpers.
   */
  const [townSections, setTownSections] = useState([]);
  const [debtSections, setDebtSections] = useState([]);
  const [educationSections, setEducationSections] = useState([]);

  /**
   * Revenue rows are combined into one CommonTable dataset.
   * These rows are constructed from multiple Firestore docs (REVENUE_DOCS).
   */
  const [revenueRows, setRevenueRows] = useState([]);
  const [revenueLoading, setRevenueLoading] = useState(false);
  const [revenueError, setRevenueError] = useState("");

  /**
   * Which Firestore revenue docs should be included in the workshop.
   *
   * NOTE: docId must match Firestore document ID exactly.
   * NOTE: `fullName` is used both for display and for rollup identification in some strategies.
   */
  const REVENUE_DOCS = [
    { category: "taxes", docId: "revenues_tax_revenues", fullName: "Tax Revenues" },
    {
      category: "licensesAndPermits",
      docId: "revenues_licenses_permits",
      fullName: "Licenses & Permits",
    },
    {
      category: "intergovernmental",
      docId: "revenues_intergovernmental",
      fullName: "Intergovernmental",
    },
    {
      category: "chargesForServices",
      docId: "revenues_charges_for_services",
      fullName: "Charges for Services",
    },
    {
      category: "otherRevenues",
      docId: "revenues_other_revenues",
      fullName: "Other Revenues",
    },
    {
      category: "transfersIn",
      docId: "revenues_transfers_in_from",
      fullName: "Transfers In From",
    },
  ];

  /**
   * External “truth” sources:
   * - `useMillRate` can return CT or configured mill rate data (depending on your implementation).
   * - `useGrandListHistory` provides net grand list values for mill rate delta calculation.
   */
  const {
    millRate: ctMillRate,
    loading: ctMillLoading,
    error: ctMillError,
  } = useMillRate(open ? town : null);

  const {
    data: grandListHistory,
    loading: grandListLoading,
    error: grandListError,
  } = useGrandListHistory(open ? town : null);

  /**
   * Choose latest grand list entry for the top-of-modal metrics.
   */
  const latestGrandList = useMemo(() => {
    if (!Array.isArray(grandListHistory) || grandListHistory.length === 0) return null;

    return grandListHistory.reduce((acc, row) => {
      const y = Number(row?.fiscalYear);
      if (!Number.isFinite(y)) return acc;
      if (!acc) return row;
      const accY = Number(acc?.fiscalYear);
      return y > accY ? row : acc;
    }, null);
  }, [grandListHistory]);

  const latestNetGrandList = latestGrandList?.totalNetGrandList ?? null;
  const latestGrandListYear = latestGrandList?.fiscalYear ?? null;

  /**
   * Anchor year resolution.
   */
  const [resolvedAnchorYear, setResolvedAnchorYear] = useState(null);
  const [anchorResolvedOnce, setAnchorResolvedOnce] = useState(false);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    const YEAR_KEY_RE = /^\d{4}$/;

    const parseYearKeys = (docObj) => {
      return Object.keys(docObj || {})
        .filter((k) => YEAR_KEY_RE.test(k))
        .map((k) => Number(k))
        .filter((n) => Number.isFinite(n));
    };

    const getEligibleYears = (docObj) => {
      const years = parseYearKeys(docObj);
      const eligible = [];

      for (const y of years) {
        const bucket = docObj?.[String(y)];
        if (!bucket || typeof bucket !== "object") continue;

        const hasApproved = bucket?.Approved && typeof bucket.Approved === "object";
        const hasAdopted = bucket?.Adopted && typeof bucket.Adopted === "object";
        const hasActual = bucket?.Actual && typeof bucket.Actual === "object";

        if (hasApproved || hasAdopted || hasActual) eligible.push(y);
      }

      return eligible;
    };

    const maxEligibleYear = (docObj) => {
      const eligible = getEligibleYears(docObj);
      return eligible.length ? Math.max(...eligible) : null;
    };

    (async () => {
      try {
        const expPaths = [
          { collection: "townGovernment", id: "expenditures_town", label: "expenditures_town" },
          {
            collection: "townGovernment",
            id: "expenditures_debt_transfers_out",
            label: "expenditures_debt_transfers_out",
          },
          {
            collection: "townGovernment",
            id: "expenditures_education",
            label: "expenditures_education",
          },
        ];

        const revPaths = REVENUE_DOCS.map((d) => ({
          collection: "townGovernment",
          id: d.docId,
          label: d.docId,
        }));

        const allPaths = [...expPaths, ...revPaths];

        const snaps = await Promise.all(
          allPaths.map(async (p) => {
            try {
              const ref = doc(db, p.collection, p.id);
              const snap = await getDoc(ref);
              const raw = snap.exists() ? snap.data() : null;
              return { ...p, snap, raw, fetchError: null };
            } catch (e) {
              return { ...p, snap: null, raw: null, fetchError: e };
            }
          })
        );

        if (cancelled) return;

        const perSource = snaps.map((x) => {
          if (x.fetchError) {
            return {
              label: x.label,
              collection: x.collection,
              id: x.id,
              exists: false,
              fetchError: x.fetchError,
              yearKeys: [],
              eligibleYears: [],
              maxEligibleYear: null,
            };
          }

          const exists = !!x.snap?.exists?.();
          const raw = exists ? x.raw : null;

          const yearKeys = raw
            ? Object.keys(raw)
                .filter((k) => YEAR_KEY_RE.test(k))
                .map((k) => Number(k))
            : [];

          const eligibleYears = raw ? getEligibleYears(raw) : [];
          const maxY = raw ? maxEligibleYear(raw) : null;

          return {
            label: x.label,
            collection: x.collection,
            id: x.id,
            exists,
            fetchError: null,
            yearKeys,
            eligibleYears,
            maxEligibleYear: maxY,
          };
        });

        const missingDocs = perSource.filter((p) => !p.exists);
        const emptyEligible = perSource.filter(
          (p) => p.exists && (!p.eligibleYears || p.eligibleYears.length === 0)
        );

        if (missingDocs.length || emptyEligible.length) {
          const msg =
            "[BuildBudgetModal][Anchor] FATAL: Cannot compute common year because one or more source docs are missing or have no eligible years.";
          console.error(msg, {
            missingDocs: missingDocs.map((d) => `${d.label} (${d.collection}/${d.id})`),
            emptyEligible: emptyEligible.map((d) => `${d.label} (${d.collection}/${d.id})`),
            perSource,
          });

          setResolvedAnchorYear(null);
          setError(
            "Build Budget Workshop cannot determine a common anchor year. One or more required documents are missing or have no usable year data."
          );
          setAnchorResolvedOnce(true);
          return;
        }

        const maxes = perSource
          .map((p) => p.maxEligibleYear)
          .filter((y) => Number.isFinite(y));

        const common = maxes.length ? Math.min(...maxes) : null;

        if (!Number.isFinite(common)) {
          const msg =
            "[BuildBudgetModal][Anchor] FATAL: Computed common anchor year is null/invalid.";
          console.error(msg, { maxes, common, perSource });

          setResolvedAnchorYear(null);
          setError(
            "Build Budget Workshop could not compute a valid common anchor year from the underlying documents."
          );
          setAnchorResolvedOnce(true);
          return;
        }

        setResolvedAnchorYear(common);
        setAnchorResolvedOnce(true);
      } catch (e) {
        console.error("[BuildBudgetModal][Anchor] FATAL: Failed to resolve common anchor year:", e);
        setResolvedAnchorYear(null);
        setError(
          "Build Budget Workshop failed while determining the common anchor year. Check console logs for details."
        );
        setAnchorResolvedOnce(true);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialYear]);

  /**
   * currentYear is the resolved “anchor” year.
   */
  const currentYear = useMemo(() => {
    const initRaw = initialYear;
    const init = Number(initRaw);

    const resolvedRaw = resolvedAnchorYear;
    const resolved =
      resolvedRaw === null || resolvedRaw === undefined ? NaN : Number(resolvedRaw);

    if (!anchorResolvedOnce) return NaN;

    if (!Number.isFinite(resolved)) {
      const msg =
        "[BuildBudgetModal][Year] FATAL: resolvedAnchorYear is null/invalid after resolution. Cannot run workshop.";
      console.error(msg, { initialYear: initRaw, resolvedAnchorYear: resolvedRaw });
      throw new Error(msg);
    }

    if (Number.isFinite(init) && init !== resolved) {
      console.warn(
        "[BuildBudgetModal][Year] initialYear differs from resolved anchor year; ignoring initialYear.",
        { initialYear: init, resolvedAnchorYear: resolved }
      );
    }

    return resolved;
  }, [initialYear, resolvedAnchorYear, anchorResolvedOnce]);

  /**
   * Columns for the Build Budget Workshop.
   */
  const { columns } = useMemo(() => {
    if (!Number.isFinite(currentYear)) return { columns: [], baseColumns: [] };
    return buildBuildBudgetColumns(currentYear);
  }, [currentYear]);

  /**
   * Base columns passed to fetchers.
   */
  const fetchBaseColumns = useMemo(() => {
    if (!Number.isFinite(currentYear)) return [{ key: "label" }];

    return [
      { key: "label" },
      { year: currentYear - 2, type: "actual", key: "y2_actual" },
      { year: currentYear - 1, type: "adopted", key: "y1_adopted" },
      { year: currentYear, type: "adopted", key: "y0_adopted" },
    ];
  }, [currentYear]);

  /**
   * Determine which baseline mill rate to display:
   */
  const baselineDisplayedMillRate = useMemo(
    () => pickBaselineMillRate(ctMillRate, baseMillRate),
    [ctMillRate, baseMillRate]
  );

  /**
   * Revenue cell edit handler:
   */
  const handleRevenueChange = useCallback((e, rowLabel, colKey) => {
    const nextVal = parseCurrencyLike(e?.target?.value);

    setRevenueRows((prev) =>
      (prev || []).map((r) => {
        if (r?.section) return r;
        if (r?.label !== rowLabel) return r;
        return { ...r, [colKey]: nextVal };
      })
    );
  }, []);

  /**
   * Escape key closes the modal.
   */
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  /**
   * Load baseline mill rate doc.
   */
  useEffect(() => {
    if (!open) return;
    if (!Number.isFinite(currentYear)) return;

    let alive = true;

    (async () => {
      try {
        setError("");

        const millSnap = await getDoc(doc(db, millRateDocPath.collection, millRateDocPath.id));
        if (!alive) return;

        const millData = millSnap.exists() ? millSnap.data() : null;

        const mill =
          (typeof millData?.value === "number" && millData.value) ||
          (typeof millData?.millRate === "number" && millData.millRate) ||
          (typeof millData?.years?.[String(currentYear)] === "number" &&
            millData.years[String(currentYear)]) ||
          null;

        setBaseMillRate(mill);
      } catch (e) {
        console.error("[BuildBudgetModal] Failed to load mill rate:", e);
      }
    })();

    return () => {
      alive = false;
    };
  }, [open, millRateDocPath.collection, millRateDocPath.id, currentYear]);

  /**
   * Load Current Levy baseline from Tax Revenues doc.
   */
  useEffect(() => {
    if (!open) return;

    let alive = true;

    (async () => {
      setLevyLoading(true);
      try {
        const snap = await getDoc(doc(db, taxRevenuesDocPath.collection, taxRevenuesDocPath.id));
        if (!alive) return;

        if (!snap.exists()) {
          console.warn("[BuildBudgetModal] Tax Revenues doc not found:", taxRevenuesDocPath);
          setStoredCurrentLevy(null);
          return;
        }

        const data = snap.data();

        const levy = pickLatestCurrentLevy(data);
        setStoredCurrentLevy(typeof levy === "number" && Number.isFinite(levy) ? levy : null);

        const yearNums = Object.keys(data || {})
          .map((k) => Number(k))
          .filter((n) => Number.isFinite(n));

        const maxYear = yearNums.length ? Math.max(...yearNums) : null;
        setDataMaxYear(maxYear);
      } catch (e) {
        console.error("[BuildBudgetModal] Failed to load Current Levy from Tax Revenues:", e);
        setStoredCurrentLevy(null);
      } finally {
        if (alive) setLevyLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [open, taxRevenuesDocPath.collection, taxRevenuesDocPath.id]);

  /**
   * Load expenditure sections (Town, Debt, Education).
   */
  useEffect(() => {
    if (!open) return;
    if (!Number.isFinite(currentYear)) return;
    if (error) return;

    let cancelled = false;

    (async () => {
      setLoading(true);

      try {
        const [townData, debtData, eduData] = await Promise.all([
          fetchExpenditureSections("town", fetchBaseColumns),
          fetchExpenditureSections("debtServiceAndTransfers", fetchBaseColumns),
          fetchExpenditureSections("education", fetchBaseColumns),
        ]);

        if (cancelled) return;

        const nextTown = seedBuildBudget(applySectionRowOrder(townData.sections, "town"));
        const nextDebt = seedBuildBudget(
          applySectionRowOrder(debtData.sections, "debtServiceAndTransfers")
        );
        const nextEdu = seedBuildBudget(applySectionRowOrder(eduData.sections, "education"));

        setTownSections(nextTown);
        setDebtSections(nextDebt);
        setEducationSections(nextEdu);
      } catch (e) {
        console.error("[BuildBudgetModal] Failed to load expenditure sections:", e);
        if (!cancelled) setError("Failed to load expenditure sections.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, fetchBaseColumns, currentYear, error]);

  /**
   * Load revenue docs.
   */
  useEffect(() => {
    if (!open) return;
    if (!Number.isFinite(currentYear)) return;
    if (error) return;

    let cancelled = false;

    (async () => {
      setRevenueLoading(true);
      setRevenueError("");

      try {
        const results = await Promise.all(
          REVENUE_DOCS.map(async (d) => {
            const res = await fetchRevenueData(d.category, fetchBaseColumns, d.docId);
            return { def: d, res };
          })
        );

        if (cancelled) return;

        let rows = [];

        for (const { def, res } of results) {
          const data = res?.rawDoc || null;
          rows = rows.concat(
            buildRevenueRowsFromDoc(data, def.fullName, currentYear).map((r) => ({
              ...r,
              __revGroup: def.category,
            }))
          );
        }

        setRevenueRows(seedBuildBudget(rows));
      } catch (e) {
        console.error("[BuildBudgetModal] Failed to load revenue docs:", e);
        if (!cancelled) setRevenueError("Failed to load revenue tables.");
      } finally {
        if (!cancelled) setRevenueLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fetchBaseColumns, currentYear, error]);

  /**
   * Section change handlers.
   */
  const handleTownChange = useMemo(() => createSectionChangeHandler(setTownSections), []);
  const handleDebtChange = useMemo(() => createSectionChangeHandler(setDebtSections), []);

  /**
   * Enforce workshop editing rules.
   */
  const guardOnChange = useCallback(
    (inner) => (e, rowLabel, colKey) => {
      if (colKey !== "buildBudget") return;
      const lbl = String(rowLabel || "").trim().toUpperCase();
      if (lbl === "CURRENT LEVY") return; // hard-lock Current Levy
      inner(e, rowLabel, colKey);
    },
    []
  );

  /**
   * TOWN display dataset.
   */
  const townDisplayData = useMemo(() => {
    const base = buildIntergovDisplayData(townSections, columns);
    const seeded = seedBuildBudget(base);

    const normalized = seeded.map((row) =>
      typeof row.label === "string" && row.label.toUpperCase().startsWith("TOTAL INTERGOVERNMENTAL")
        ? { ...row, label: "TOTAL TOWN" }
        : row
    );

    const withBuildTotals = populateBuildBudgetTotals(normalized, {
      grandTotalLabels: ["TOTAL TOWN"],
    });

    return applyBuildBudgetDelta(withBuildTotals);
  }, [townSections, columns]);

  /**
   * DEBT display dataset.
   */
  const debtDisplayData = useMemo(() => {
    const base = buildIntergovDisplayData(debtSections, columns);
    const seeded = seedBuildBudget(base);

    const normalized = seeded.map((row) =>
      typeof row.label === "string" && row.label.toUpperCase().startsWith("TOTAL INTERGOVERNMENTAL")
        ? { ...row, label: "TOTAL DEBT SERVICE & TRANSFERS OUT" }
        : row
    );

    const withBuildTotals = populateBuildBudgetTotals(normalized, {
      grandTotalLabels: ["TOTAL DEBT SERVICE & TRANSFERS OUT"],
    });

    return applyBuildBudgetDelta(withBuildTotals);
  }, [debtSections, columns]);

  /**
   * EDUCATION display dataset.
   */
  const educationDisplayData = useMemo(() => {
    const base = buildIntergovDisplayData(educationSections, columns);
    const seeded = seedBuildBudget(base);

    const isSectionRow = (r) => !!r?.section;
    const sectionRows = (seeded || []).filter(isSectionRow);
    const firstDataRow = (seeded || []).find((r) => !isSectionRow(r)) || null;

    const singleRow = firstDataRow
      ? {
          ...firstDataRow,
          label: "TOTAL EDUCATION",
          bold: true,
          buildBudget:
            typeof firstDataRow?.y0_adopted === "number" && Number.isFinite(firstDataRow.y0_adopted)
              ? firstDataRow.y0_adopted
              : undefined,
        }
      : null;

    const compact = [...(sectionRows || []), ...(singleRow ? [singleRow] : [])];
    return applyBuildBudgetDelta(compact);
  }, [educationSections, columns]);

  /**
   * Expenditure totals (MUST be declared BEFORE revenueDisplayData to avoid TDZ issues).
   */
  const expenditureTotals = useMemo(() => {
    return computeWorkshopTotals(
      [...townDisplayData, ...debtDisplayData, ...educationDisplayData],
      ["TOTAL TOWN", "TOTAL DEBT SERVICE & TRANSFERS OUT", "TOTAL EDUCATION"]
    );
  }, [townDisplayData, debtDisplayData, educationDisplayData]);

  /**
   * Revenue base display + totals excluding levy.
   */
  const revenueDisplayDataBase = useMemo(() => {
    const seeded = seedBuildBudget(revenueRows);
    const withSectionTotals = injectRevenueSectionTotals(seeded);

    const sumKeys = ["y2_actual", "y1_adopted", "y0_adopted", "buildBudget"];
    const norm = (s) => String(s || "").trim().toUpperCase();
    const isSectionRow = (r) => !!r?.section;
    const isTotalLike = (label) => norm(label).startsWith("TOTAL");

    const addRowInto = (acc, row) => {
      for (const k of sumKeys) {
        const v = row?.[k];
        if (typeof v === "number" && Number.isFinite(v)) acc[k] += v;
      }
    };

    const grandTotals = {};
    for (const k of sumKeys) grandTotals[k] = 0;

    for (const def of REVENUE_DOCS || []) {
      const group = def?.category;
      const rollupLabel = def?.fullName;

      const groupRows = (withSectionTotals || []).filter(
        (r) => !isSectionRow(r) && r?.__revGroup === group
      );

      const rollupRow = groupRows.find((r) => norm(r?.label) === norm(rollupLabel)) || null;

      if (rollupRow) {
        addRowInto(grandTotals, rollupRow);
        continue;
      }

      const leafRows = groupRows.filter((r) => {
        const lbl = r?.label;
        if (isTotalLike(lbl)) return false;
        if (norm(lbl) === norm(rollupLabel)) return false;
        return true;
      });

      for (const r of leafRows) addRowInto(grandTotals, r);
    }

    const rowsWithoutGrandTotal = (withSectionTotals || []).filter(
      (r) => isSectionRow(r) || norm(r?.label) !== "TOTAL REVENUES"
    );

    const totalRow = { label: "TOTAL REVENUES", bold: true, ...grandTotals };
    const merged = [...rowsWithoutGrandTotal, totalRow];

    return applyBuildBudgetDelta(merged);
  }, [revenueRows, REVENUE_DOCS]);

const revenueTotalsExcludingLevy = useMemo(() => {
  const norm = (s) => String(s || "").trim().toUpperCase();
  const isSectionRow = (r) => !!r?.section;
  const isTotalLike = (label) => norm(label).startsWith("TOTAL");
  const isCurrentLevy = (label) => norm(label) === "CURRENT LEVY";

  let baseline = 0;
  let build = 0;

  // Sum ONLY leaf revenue lines, grouped by revenue doc (prevents rollup double counts)
  for (const def of REVENUE_DOCS || []) {
    const group = def?.category;
    const rollupLabel = def?.fullName; // e.g. "Tax Revenues"

    const groupRows = (revenueDisplayDataBase || []).filter(
      (r) => !isSectionRow(r) && r?.__revGroup === group
    );

    // Leaf rows = not TOTAL..., not doc rollup row, not CURRENT LEVY
    const leafRows = groupRows.filter((r) => {
      const lbl = r?.label;
      if (isTotalLike(lbl)) return false;
      if (norm(lbl) === norm(rollupLabel)) return false; // exclude doc rollup row
      if (isCurrentLevy(lbl)) return false;              // exclude balancing line
      return true;
    });

    for (const r of leafRows) {
      const b0 = r?.y0_adopted;
      const bb = r?.buildBudget;

      if (typeof b0 === "number" && Number.isFinite(b0)) baseline += b0;
      if (typeof bb === "number" && Number.isFinite(bb)) build += bb;
    }
  }

  return {
    baseline,
    build,
    change: build - baseline, // POSITIVE when non-levy revenues increase
  };
}, [revenueDisplayDataBase, REVENUE_DOCS]);

  /**
   * Revenue display dataset (locks Current Levy row to computed levy).
   * NOTE: This is now safe because expenditureTotals is declared above.
   */
const revenueDisplayData = useMemo(() => {
  const norm = (s) => String(s || "").trim().toUpperCase();
  const isSectionRow = (r) => !!r?.section;
  const isCurrentLevy = (label) => norm(label) === "CURRENT LEVY";
  const isTotalLike = (label) => norm(label).startsWith("TOTAL");

  // --- Compute net levy delta locally (avoid TDZ / ordering issues) ---
  const expChange = expenditureTotals?.change;
  const revChange = revenueTotalsExcludingLevy?.change;

  const expOk = typeof expChange === "number" && Number.isFinite(expChange);
  const revOk = typeof revChange === "number" && Number.isFinite(revChange);

  const computedNetLevyDelta =
    !expOk && !revOk ? null : (expOk ? expChange : 0) - (revOk ? revChange : 0);

  // Compute levy locally (no dependency on outer `netLevyDelta`)
  const computedLevy = computeUpdatedLevy(storedCurrentLevy, computedNetLevyDelta);

  // 1) Lock CURRENT LEVY row's buildBudget
  const withLockedLevy = (revenueDisplayDataBase || []).map((r) => {
    if (isSectionRow(r)) return r;
    if (!isCurrentLevy(r?.label)) return r;

    const nextBuild =
      typeof computedLevy === "number" && Number.isFinite(computedLevy)
        ? computedLevy
        : r?.buildBudget;

    return {
      ...r,
      buildBudget: nextBuild,
      isLocked: true,
    };
  });

  // 2) Rebuild TOTAL REVENUES using "one total per revenue doc group" strategy
  //    (prevents double counting rollups + leaves + section totals)
  const sumKeys = ["y2_actual", "y1_adopted", "y0_adopted", "buildBudget"];

  const addRowInto = (acc, row) => {
    for (const k of sumKeys) {
      const v = row?.[k];
      if (typeof v === "number" && Number.isFinite(v)) acc[k] += v;
    }
  };

  const grandTotals = {};
  for (const k of sumKeys) grandTotals[k] = 0;

  for (const def of REVENUE_DOCS || []) {
    const group = def?.category;
    const rollupLabel = def?.fullName; // e.g. "Tax Revenues"

    // Only rows belonging to this doc group and not section headers
    const groupRows = (withLockedLevy || []).filter(
      (r) => !isSectionRow(r) && r?.__revGroup === group
    );

    // Prefer to compute group totals from leaf rows to guarantee CURRENT LEVY flows in,
    // while excluding:
    // - ANY "TOTAL ..." rows (section totals and grand totals)
    // - The doc rollup row itself (label === def.fullName)
    const leafRows = groupRows.filter((r) => {
      const lbl = r?.label;
      if (isTotalLike(lbl)) return false;
      if (norm(lbl) === norm(rollupLabel)) return false; // exclude the rollup row
      return true;
    });

    if (leafRows.length) {
      // Sum leaves (includes CURRENT LEVY if it is a leaf row in this group)
      for (const r of leafRows) addRowInto(grandTotals, r);
      continue;
    }

    // Fallback: if we somehow have no leaves, use the rollup row (still avoid totals)
    const rollupRow =
      groupRows.find((r) => norm(r?.label) === norm(rollupLabel)) || null;

    if (rollupRow) addRowInto(grandTotals, rollupRow);
  }

  // 3) Replace existing TOTAL REVENUES row with the new grand total
  const rowsWithoutGrandTotal = (withLockedLevy || []).filter(
    (r) => isSectionRow(r) || norm(r?.label) !== "TOTAL REVENUES"
  );

  const merged = [
    ...rowsWithoutGrandTotal,
    { label: "TOTAL REVENUES", bold: true, ...grandTotals },
  ];

  return applyBuildBudgetDelta(merged);
}, [
  revenueDisplayDataBase,
  storedCurrentLevy,
  expenditureTotals?.change,
  revenueTotalsExcludingLevy?.change,
  REVENUE_DOCS,
]);

  /**
   * Revenue totals (from minted TOTAL REVENUES row).
   */
  const revenueTotals = useMemo(() => {
    const totalRow = (revenueDisplayData || []).find((r) => !r?.section && r?.label === "TOTAL REVENUES");
    const baseline = totalRow?.y0_adopted;
    const build = totalRow?.buildBudget;

    const baselineOk = typeof baseline === "number" && Number.isFinite(baseline);
    const buildOk = typeof build === "number" && Number.isFinite(build);

    if (baselineOk && buildOk) return { baseline, build, change: build - baseline };
    return computeWorkshopTotals(revenueDisplayData, ["TOTAL REVENUES"]);
  }, [revenueDisplayData]);

  /**
   * Net levy delta:
   * netLevyDelta = expenditureChange - (non-levy revenue change)
   */
  const netLevyDelta = useMemo(() => {
    const exp = expenditureTotals?.change;
    const rev = revenueTotalsExcludingLevy?.change;

    const expOk = typeof exp === "number" && Number.isFinite(exp);
    const revOk = typeof rev === "number" && Number.isFinite(rev);

    if (!expOk && !revOk) return null;
    return (expOk ? exp : 0) - (revOk ? rev : 0);
  }, [expenditureTotals?.change, revenueTotalsExcludingLevy?.change]);

  /**
   * Updated levy = stored baseline levy + net levy delta
   */
  const currentLevy = useMemo(() => {
    return computeUpdatedLevy(storedCurrentLevy, netLevyDelta);
  }, [storedCurrentLevy, netLevyDelta]);

  /**
   * Display mill rate baseline.
   */
  const displayedMillRate = useMemo(() => {
    return typeof baselineDisplayedMillRate === "number" && Number.isFinite(baselineDisplayedMillRate)
      ? baselineDisplayedMillRate
      : null;
  }, [baselineDisplayedMillRate]);

  /**
   * Mill rate delta formula:
   * deltaMillRate = (deltaLevy / netGrandList) * 1000
   */
  const workshopMillRateDelta = useMemo(() => {
    const deltaBudget = netLevyDelta;
    const netGL = latestNetGrandList;

    if (
      typeof deltaBudget !== "number" ||
      !Number.isFinite(deltaBudget) ||
      typeof netGL !== "number" ||
      !Number.isFinite(netGL) ||
      netGL === 0
    ) {
      return null;
    }

    return (deltaBudget / netGL) * 1000;
  }, [netLevyDelta, latestNetGrandList]);

  /**
   * New mill rate = baseline + delta.
   */
  const newMillRateDisplayed = useMemo(() => {
    if (typeof displayedMillRate !== "number" || !Number.isFinite(displayedMillRate)) return null;
    if (typeof workshopMillRateDelta !== "number" || !Number.isFinite(workshopMillRateDelta)) {
      return displayedMillRate;
    }
    return displayedMillRate + workshopMillRateDelta;
  }, [displayedMillRate, workshopMillRateDelta]);

  const isNewMillRateLower = useMemo(() => {
    if (
      typeof displayedMillRate !== "number" ||
      !Number.isFinite(displayedMillRate) ||
      typeof newMillRateDisplayed !== "number" ||
      !Number.isFinite(newMillRateDisplayed)
    ) {
      return false;
    }
    return newMillRateDisplayed < displayedMillRate;
  }, [displayedMillRate, newMillRateDisplayed]);

  const showMillRateLoading = levyLoading || grandListLoading || ctMillLoading;

  /**
   * Modal not open => render nothing.
   */
  if (!open) return null;

  /**
   * While anchor year is resolving, show a lightweight modal shell.
   */
  if (!anchorResolvedOnce || !Number.isFinite(currentYear)) {
    return (
      <div className="fixed inset-0 z-[9998] flex items-center justify-center" aria-modal="true" role="dialog">
        <div className="absolute inset-0 bg-black/50" onClick={onClose} />
        <div className="relative z-[9999] w-[min(900px,94vw)] rounded-lg bg-white shadow-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="text-lg font-semibold text-gray-900">{title}</div>
            <button
              type="button"
              className="h-10 w-10 inline-flex items-center justify-center rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              onClick={onClose}
              aria-label="Close modal"
              title="Close"
            >
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>

          <div className="mt-3 text-sm text-gray-700">Resolving common fiscal year from Firestore…</div>

          <div className="mt-2 text-xs text-gray-500">
            Debug: initialYear={String(initialYear)} resolvedAnchorYear={String(resolvedAnchorYear)} dataMaxYear=
            {String(dataMaxYear)}
          </div>

          {error ? (
            <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">{error}</div>
          ) : null}
        </div>
      </div>
    );
  }

  /**
   * Main modal render.
   */
  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center" aria-modal="true" role="dialog">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div className="relative z-[9999] w-[min(1100px,94vw)] max-h-[88vh] overflow-auto rounded-lg bg-white shadow-xl border border-gray-200">
        {/* Header */}
        <div className="px-4 border-b border-gray-200 h-14 flex items-center">
          <div className="h-10 w-10" />

          <div className="flex-1 text-center">
            <div className="text-lg font-semibold text-gray-900 leading-none">{title}</div>
          </div>

          <button
            type="button"
            className="h-10 w-10 inline-flex items-center justify-center rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            onClick={onClose}
            aria-label="Close modal"
            title="Close"
          >
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        <div className="p-2 space-y-2">
          {/* Workshop metrics header (tight) */}
          <div className="flex items-start justify-between gap-2">
            {/* LEFT: metrics panel */}
            <div className="flex-1 border border-gray-200 rounded bg-gray-50 px-2 py-1.5 flex flex-col gap-0.5">
              <div className="flex items-center gap-2 text-sm">
                <span className="font-semibold text-gray-700">Current Levy:</span>
                <span className="font-medium text-gray-900 tabular-nums">
                  {levyLoading ? "Loading..." : Number.isFinite(currentLevy) ? fmtDollars0(currentLevy) : "—"}
                </span>
              </div>

              <div className="flex items-center gap-2 text-sm">
                <span className="font-semibold text-gray-700">Net Grand List:</span>
                <span className="font-medium text-gray-900 tabular-nums">
                  {grandListLoading
                    ? "Loading..."
                    : typeof latestNetGrandList === "number" && Number.isFinite(latestNetGrandList)
                    ? `${fmtDollars0(latestNetGrandList)}${latestGrandListYear ? ` (FY ${latestGrandListYear})` : ""}`
                    : grandListError
                    ? "N/A"
                    : "—"}
                </span>
              </div>
            </div>

            {/* RIGHT: Mill Rate box */}
            <div className="border border-gray-200 rounded bg-white px-2 py-1.5 shadow-sm min-w-[260px]">
              {showMillRateLoading ? (
                <div className="text-sm text-gray-700">Loading...</div>
              ) : Number.isFinite(displayedMillRate) ? (
                <div className="flex flex-col gap-0.5">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-semibold text-gray-700">Mill Rate:</span>
                    <span className="font-medium text-gray-900 tabular-nums">{fmtMillRate(displayedMillRate)}</span>
                  </div>

                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-semibold text-gray-700">New Mill Rate:</span>
                    <span
                      className={`font-medium tabular-nums ${
                        isNewMillRateLower ? "text-red-600" : "text-gray-900"
                      }`}
                    >
                      {fmtMillRate(newMillRateDisplayed)}
                    </span>
                  </div>
                </div>
              ) : ctMillError ? (
                <div className="text-sm text-gray-700">N/A</div>
              ) : (
                <div className="text-sm text-gray-700">—</div>
              )}
            </div>
          </div>

          {/* Content */}
          {loading ? (
            <div className="text-sm text-gray-600">Loading workshop tables…</div>
          ) : error ? (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">{error}</div>
          ) : (
            <div className="space-y-2">
              <CommonTable
                title="Expenses"
                columns={columns}
                data={townDisplayData}
                isEditing={true}
                headerColor="bg-[#d5e7f7]"
                onChange={guardOnChange(handleTownChange)}
                showAggregateTotal={false}
                sectionOrder={TOWN_SECTION_ORDER}
                editOnCellClick={true}
                commitOnBlur={true}
              />

              <CommonTable
                columns={columns}
                data={debtDisplayData}
                isEditing={true}
                headerColor="bg-[#d5e7f7]"
                onChange={guardOnChange(handleDebtChange)}
                showAggregateTotal={false}
                editOnCellClick={true}
                commitOnBlur={true}
              />

              <CommonTable
                columns={columns}
                data={educationDisplayData}
                isEditing={false}
                headerColor="bg-[#d5e7f7]"
                showAggregateTotal={false}
                editOnCellClick={true}
                commitOnBlur={true}
              />

              {/* Revenues */}
              {revenueLoading ? (
                <div className="text-sm text-gray-600">Loading revenue tables…</div>
              ) : revenueError ? (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">{revenueError}</div>
              ) : (
                <CommonTable
                  title="Revenues"
                  columns={columns}
                  data={revenueDisplayData}
                  isEditing={true}
                  headerColor="bg-[#d5f7e3]"
                  onChange={guardOnChange(handleRevenueChange)}
                  showAggregateTotal={false}
                  editOnCellClick={true}
                  commitOnBlur={true}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
